import { useCallback, useState } from 'react';

// Compression threshold: only compress videos larger than 10MB
const COMPRESSION_THRESHOLD_MB = 10;
const COMPRESSION_THRESHOLD_BYTES = COMPRESSION_THRESHOLD_MB * 1024 * 1024;

// Max resolution: 1080p
const MAX_HEIGHT = 1080;
const MAX_WIDTH = 1920;

// Default bitrates (used when no target size is specified)
const DEFAULT_VIDEO_BITRATE = 4_000_000; // 4 Mbps for smooth playback
const AUDIO_BITRATE = 128_000; // 128 kbps

// Minimum video bitrate to avoid unwatchable quality
const MIN_VIDEO_BITRATE = 500_000; // 0.5 Mbps

export type CompressionProgress = {
  stage: 'loading' | 'compressing' | 'done' | 'error';
  progress: number;
  message: string;
};

export type CompressionResult = {
  file: File;
  originalSize: number;
  compressedSize: number;
  wasCompressed: boolean;
  error?: string;
};

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => void;
};

export const useVideoCompression = () => {
  const [isCompressing, setIsCompressing] = useState(false);
  const [progress, setProgress] = useState<CompressionProgress>({
    stage: 'done',
    progress: 0,
    message: '',
  });

  const compressVideo = useCallback(
    async (file: File, targetSizeMB?: number): Promise<CompressionResult> => {
      const originalSize = file.size;

      const thresholdBytes = targetSizeMB
        ? targetSizeMB * 1024 * 1024
        : COMPRESSION_THRESHOLD_BYTES;

      if (originalSize <= thresholdBytes) {
        return {
          file,
          originalSize,
          compressedSize: originalSize,
          wasCompressed: false,
        };
      }

      setIsCompressing(true);
      setProgress({
        stage: 'loading',
        progress: 0,
        message: 'Loading video...',
      });

      try {
        const video: VideoWithFrameCallback = document.createElement('video');
        video.muted = true;
        video.playsInline = true;

        const videoUrl = URL.createObjectURL(file);
        video.src = videoUrl;

        await new Promise<void>((resolve, reject) => {
          video.onloadedmetadata = () => resolve();
          video.onerror = () => reject(new Error('Failed to load video'));
        });

        let { videoWidth: width, videoHeight: height } = video;
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const scale = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        width -= width % 2;
        height -= height % 2;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable');

        const canvasStream = canvas.captureStream(60);

        let combinedStream: MediaStream;
        try {
          const audioCtx = new AudioContext();
          const source = audioCtx.createMediaElementSource(video);
          const destination = audioCtx.createMediaStreamDestination();
          source.connect(destination);
          source.connect(audioCtx.destination);

          combinedStream = new MediaStream([
            ...canvasStream.getVideoTracks(),
            ...destination.stream.getAudioTracks(),
          ]);
        } catch {
          combinedStream = canvasStream;
        }

        const mimeType = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')
          ? 'video/mp4;codecs=avc1'
          : MediaRecorder.isTypeSupported('video/mp4')
            ? 'video/mp4'
            : MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
              ? 'video/webm;codecs=vp9'
              : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
                ? 'video/webm;codecs=vp8'
                : 'video/webm';

        const fileExtension = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';

        let videoBitrate = DEFAULT_VIDEO_BITRATE;
        if (targetSizeMB && video.duration > 0) {
          const targetBytes = targetSizeMB * 1024 * 1024;
          const targetTotalBitrate = (targetBytes * 8) / video.duration;
          videoBitrate = Math.max(
            MIN_VIDEO_BITRATE,
            Math.floor(targetTotalBitrate - AUDIO_BITRATE)
          );
        }

        const mediaRecorder = new MediaRecorder(combinedStream, {
          mimeType,
          videoBitsPerSecond: videoBitrate,
          audioBitsPerSecond: AUDIO_BITRATE,
        });

        const chunks: Blob[] = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        const recordingPromise = new Promise<Blob>((resolve, reject) => {
          mediaRecorder.onstop = () => {
            const blob = new Blob(chunks, { type: mimeType });
            resolve(blob);
          };
          mediaRecorder.onerror = (e) => reject(e);
        });

        setProgress({
          stage: 'compressing',
          progress: 0,
          message: 'Compressing video...',
        });

        mediaRecorder.start(100);

        video.currentTime = 0;
        const duration = video.duration;

        const drawFrame = () => {
          if (video.ended || video.paused) return;

          ctx.drawImage(video, 0, 0, width, height);

          const currentProgress = Math.min(
            99,
            Math.round((video.currentTime / duration) * 100)
          );
          setProgress({
            stage: 'compressing',
            progress: currentProgress,
            message: `Compressing video... ${currentProgress}%`,
          });

          if (video.requestVideoFrameCallback) {
            video.requestVideoFrameCallback(drawFrame);
          } else {
            requestAnimationFrame(drawFrame);
          }
        };

        if (video.requestVideoFrameCallback) {
          video.requestVideoFrameCallback(drawFrame);
        } else {
          requestAnimationFrame(drawFrame);
        }

        await video.play();

        await new Promise<void>((resolve) => {
          video.onended = () => resolve();
        });

        mediaRecorder.stop();
        const compressedBlob = await recordingPromise;

        URL.revokeObjectURL(videoUrl);
        video.remove();
        canvas.remove();

        const compressedSize = compressedBlob.size;

        const baseName = file.name.replace(/\.[^/.]+$/, '');
        const compressedFile = new File(
          [compressedBlob],
          `${baseName}_compressed.${fileExtension}`,
          { type: mimeType, lastModified: Date.now() }
        );

        setProgress({
          stage: 'done',
          progress: 100,
          message: 'Compression complete!',
        });

        if (compressedSize >= originalSize) {
          return {
            file,
            originalSize,
            compressedSize: originalSize,
            wasCompressed: false,
          };
        }

        return {
          file: compressedFile,
          originalSize,
          compressedSize,
          wasCompressed: true,
        };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Compression failed';
        console.error('Video compression failed:', errorMessage, error);
        setProgress({
          stage: 'error',
          progress: 0,
          message: errorMessage,
        });

        return {
          file,
          originalSize,
          compressedSize: originalSize,
          wasCompressed: false,
          error: errorMessage,
        };
      } finally {
        setIsCompressing(false);
      }
    },
    []
  );

  return { compressVideo, isCompressing, progress };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};
